import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Drawer,
  Row,
  Col,
  Typography,
  Form,
  Button,
  message,
  Steps,
} from "antd";
import { appointmentSteps } from "../receptionKeys";
import { CloseOutlined } from "@ant-design/icons";
import AppointmentFormList from "./AppointmentFormList";
import PatientDetailsForm from "./PatientDetailsForm";
import {
  addPatientApi,
  getPatientDetailsApi,
  updateAppointmentApi,
  updatePatientApi,
} from "../../../services/receptionist.services";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import { FacilityID } from "../../../constants/defaultKeys";
import { getCookie } from "../../../helpers/localStorage";
import { UserContext } from "../../../context/UserContext";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import "../receptionistMobileForms.css";
import styles from "./EditAppointmentModal.module.css";

const EditAppointmentModal = ({ open, setOpen, editData, refresh }) => {
  const isMobile = useIsMobile();
  const { userDetails } = useContext(UserContext);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [patientForm] = Form.useForm();
  const patientFormRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [searchedText, setSearchedText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEndSlot, setSelectedEndSlot] = useState(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (editData?.id) {
      form?.setFieldsValue({
        ...editData,
        appointment_date: dayjs(editData?.appointment_date),
        name: `${capitalizeFirstLetter(
          editData?.doctor_name.split(" ")[0]
        )} ${capitalizeFirstLetter(editData?.doctor_name.split(" ")[1])}`,
        patient_mobile: editData?.patient_mobile,
      });
      if (!editData?.patient) {
        patientForm?.setFieldsValue({
          first_name: editData?.patient_first_name,
          last_name: editData?.patient_last_name,
          age_by_dob: editData?.patient_age,
          gender: editData?.patient_gender,
          mobile: editData?.patient_mobile,
        });
      }
      setFacilityId(editData?.facility);
      setSelectedPractitioner(editData?.doctor);
      setSelectedId(editData?.patient);
      setSelectedSlot(
        editData?.appointment_time.split(":").slice(0, 2).join(":")
      );
      setSelectedEndSlot(editData?.appointment_end_time);
    }
  }, [editData?.id]);

  useEffect(() => {
    if (editData?.patient) {
      getPatientDetailsApi(editData?.patient)
        .then((res) => {
          patientForm?.setFieldsValue({
            ...res?.data,
            dob: res?.data?.dob ? dayjs(res.data.dob) : null,
            dummyMobile: `${editData?.patient_mobile}(${editData?.patient_first_name})`,
            first_name: editData?.patient_first_name,
            last_name: editData?.patient_last_name,
            age: editData?.patient_age,
            gender: editData?.patient_gender,
          });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [editData?.patient]);

  const onPatientFinish = (values) => {
    setLoading(true);
    const { dob, ...formData } = values;

    if (dob) {
      formData.dob = dayjs(dob).format("YYYY-MM-DD");
    }

    formData.mobile = editData?.patient_mobile;
    formData.facility =
      Number(getCookie(FacilityID)) || userDetails?.currFacility;
    if (editData?.patient || selectedId) {
      updatePatientApi(editData?.patient || selectedId, formData)
        .then((res) => {
          message.success(res?.data?.message);
          setLoading(false);
          setCurrent(1);
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setLoading(false);
        });
    } else {
      addPatientApi(formData)
        .then((res) => {
          message.success(res?.data?.message);
          setLoading(false);
          setCurrent(1);
          setSelectedId(res?.data?.patient_id);
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setLoading(false);
        });
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      const { dummyMobile, name, appointment_date, ...formData } = values;

      const formattedAppointmentDate = appointment_date
        ? dayjs(appointment_date).format("YYYY-MM-DD")
        : editData?.appointment_date;

      const updatedFormData = {
        ...formData,
        appointment_date: formattedAppointmentDate,
        doctor: selectedPractitioner,
        appointment_time: selectedSlot,
        appointment_end_time: selectedEndSlot,
        facility: facilityId,
        patient: selectedId,
        patient_first_name: editData?.patient_first_name,
        patient_last_name: editData?.patient_last_name,
        patient_age: editData?.patient_age,
        patient_gender: editData?.patient_gender,
        patient_mobile: editData?.patient_mobile,
        appointment_id: editData?.id,
      };

      const response = await updateAppointmentApi(updatedFormData);
      message.success(response?.data?.message);
      setOpen(false);
      refresh();
    } catch (error) {
      message.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  const onValuesChange = (changedValues, values) => {
    setFormValues({ ...values, ...changedValues });
  };

  const mobileStepLabels = ["Patient", "Appointment"];

  const renderMobileSteps = () => (
    <div className={styles.mobileStepBar} role="tablist" aria-label="Edit steps">
      {appointmentSteps.map((step, index) => (
        <React.Fragment key={step.title}>
          <button
            type="button"
            role="tab"
            aria-selected={current === index}
            className={[
              styles.mobileStepItem,
              current === index && styles.mobileStepItemActive,
              current > index && styles.mobileStepItemCompleted,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => editData?.patient && setCurrent(index)}
            disabled={!editData?.patient && index !== current}
          >
            <span className={styles.mobileStepNumber}>{index + 1}</span>
            <span className={styles.mobileStepLabel}>
              {mobileStepLabels[index] || step.title}
            </span>
          </button>
          {index < appointmentSteps.length - 1 && (
            <span className={styles.mobileStepDivider} aria-hidden />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <Drawer
        width={isMobile ? "100%" : "70%"}
        rootClassName={
          isMobile
            ? "reception-drawer-fullscreen edit-appointment-drawer-mobile"
            : undefined
        }
        open={open}
        onClose={() => setOpen(false)}
        closeIcon={null}
        styles={
          isMobile
            ? {
                header: {
                  padding: "12px 16px 10px",
                  borderBottom: "none",
                },
                body: { padding: "12px 16px 16px" },
                footer: { padding: "12px 16px 16px", borderTop: "1px solid #eef2f7" },
              }
            : undefined
        }
        title={
          isMobile ? (
            <div className={styles.editAppointmentMobileHeader}>
              <div className="reception-drawer-mobile-title">
                <Typography
                  className="reception-drawer-mobile-title-text"
                  style={{ fontWeight: 400, fontSize: "18px" }}
                >
                  Edit Appointment
                </Typography>
                <CloseOutlined onClick={() => setOpen(false)} />
              </div>
              {renderMobileSteps()}
            </div>
          ) : (
            <Row>
              <Col span={6} className="flex-start">
                <Typography
                  style={{
                    marginLeft: "12px",
                    fontWeight: "400",
                    fontSize: "20px",
                  }}
                >
                  Edit Appointment
                </Typography>
              </Col>
              <Col span={14}>
                <Steps
                  current={current}
                  items={appointmentSteps}
                  onChange={(value) => editData?.patient && setCurrent(value)}
                />
              </Col>
              <Col span={4} className="flex-end">
                <CloseOutlined onClick={() => setOpen(false)} />
              </Col>
            </Row>
          )
        }
        footer={
          isMobile ? (
            <div className="reception-drawer-footer-row">
              <Button
                loading={loading}
                htmlType="submit"
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => {
                  current === 0
                    ? patientFormRef.current.submit()
                    : formRef.current.submit();
                }}
              >
                {current === 0 ? "Save and Proceed" : "Save"}
              </Button>
            </div>
          ) : (
            <div className="flex-end" style={{ gap: "24px" }}>
              <Button
                loading={loading}
                htmlType="submit"
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => {
                  current === 0
                    ? patientFormRef.current.submit()
                    : formRef.current.submit();
                }}
              >
                {current === 0 ? "Save and Proceed" : "Save"}
              </Button>
            </div>
          )
        }
      >
        {current === 0 && (
          <Form
            layout="vertical"
            // requiredMark={false}
            form={patientForm}
            onFinish={onPatientFinish}
            ref={patientFormRef}
          >
            <PatientDetailsForm
              searchedText={searchedText}
              setSearchedText={setSearchedText}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              editData={editData}
              form={patientForm}
            />
          </Form>
        )}
        {current === 1 && (
          <Form
            layout="vertical"
            // requiredMark={false}
            form={form}
            ref={formRef}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
          >
            <AppointmentFormList
              facilityId={facilityId}
              setFacilityId={setFacilityId}
              formValues={formValues}
              selectedPractitioner={selectedPractitioner}
              setSelectedPractitioner={setSelectedPractitioner}
              selectedSlot={selectedSlot}
              setSelectedSlot={setSelectedSlot}
              selectedEndSlot={selectedEndSlot}
              setSelectedEndSlot={setSelectedEndSlot}
              selectedId={selectedId}
              editData={editData}
              form={form} 
            />
          </Form>
        )}
      </Drawer>
    </>
  );
};

export default EditAppointmentModal;
